import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

const BaseUrl = "https://api.shabdtoday.com/";
const online = "https://api.shabdtoday.com/api/home/Opinion";
const local = "https://localhost:44351/api/home/Opinion";
const online_Count = "https://api.shabdtoday.com/api/home/update-viewcountOpinion";

const OpinionCard = () => {
    const [news, setNews] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const observer = useRef();
    const navigate = useNavigate();

    // Dark mode detect
    useEffect(() => {
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        setIsDarkMode(mq.matches);

        const handler = (e) => setIsDarkMode(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    const fetchNews = async () => {
        if (isFetching || !hasMore) return;
        setIsFetching(true);

        try {
            const res = await axios.get(`${online}?page=${page}&limit=8`);
            const newData = res.data.opinionMembersModels || [];

            if (newData.length === 0) {
                setHasMore(false);
                setIsFetching(false);
                return;
            }

            setNews(prev => {
                const existing = new Set(prev.map(x => x.id));
                const unique = newData.filter(x => !existing.has(x.id));
                return unique.length === 0 ? prev : [...prev, ...unique];
            });

            setPage(p => p + 1);
        } catch (err) {
            console.error(err);
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => { fetchNews(); }, []);

    const lastElementRef = useCallback(node => {
        if (isFetching) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) fetchNews();
        });

        if (node) observer.current.observe(node);
    }, [isFetching, hasMore]);

    const goToDetails = async (id) => {
        try { await axios.post(online_Count, { MainKhabarId: id }); } catch { }
        navigate(`/OpinionDetails/${id}`);
    };

    return (
        <>
            <Helmet>
                <title>आज की प्रमुख राय और विचार – ताज़ा ओपिनियन आर्टिकल्स हिंदी में | ShabdToday</title>
                <meta name="title" content="आज की प्रमुख राय और विचार – ताज़ा ओपिनियन आर्टिकल्स हिंदी में | ShabdToday" />
                <meta name="description" content="हिंदी में पढ़ें आज की प्रमुख राय, विशेषज्ञों के विचार, एडिटोरियल, कॉलम और देश-दुनिया पर विश्लेषण। राजनीति, समाज, अर्थव्यवस्था और वर्तमान मुद्दों पर गहराई से प्रस्तुत ओपिनियन आर्टिकल्स।" />

                <meta name="keywords" content="राय हिंदी में, विचार लेख, ओपिनियन आर्टिकल्स हिंदी, एडिटोरियल हिंदी, कॉलम हिंदी, विश्लेषण लेख, हिंदी ओपिनियन न्यूज़, आज की राय, आज के विचार" />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="ShabdToday Team" />

                <link rel="canonical" href="https://shabdtoday.com/Opinions" />

                <meta property="og:title" content="राय और विचार – आज के ताज़ा ओपिनियन आर्टिकल्स | ShabdToday" />
                <meta property="og:description" content="विशेषज्ञों की राय, एडिटोरियल और विश्लेषण हिंदी में पढ़ें।" />
                <meta property="og:url" content="https://shabdtoday.com/Opinions" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://shabdtoday.com/design/images/COVER-PAGE-NEW.webp" />

                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "CollectionPage",
                        "name": "राय और विचार – ShabdToday",
                        "url": "https://shabdtoday.com/Opinions",
                        "description": "हिंदी में राय, विचार, एडिटोरियल और विश्लेषण के प्रमुख लेख।",
                        "publisher": {
                            "@type": "Organization",
                            "name": "ShabdToday",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://shabdtoday.com/design/images/logo-s.webp"
                            }
                        }
                    })}
                </script>
            </Helmet>


            <h1 className="page-title">ओपिनियन और विचार</h1>

            <div className="opinion-list">
                {news.map((post, i) => (
                    <div
                        key={post.id}
                        className={`opinion-card ${isDarkMode ? "dark" : "light"}`}
                        ref={news.length === i + 1 ? lastElementRef : null}
                    >
                        <div className="opinion-image" onClick={() => goToDetails(post.id)}>
                            <img src={BaseUrl + post.opinion_Image} alt={post.opinion_Heading} loading="lazy" />
                        </div>

                        <div className="opinion-content">
                            <div className="opinion-title-wrapper">
                                <h3 className="opinion-tag" onClick={() => goToDetails(post.id)}>
                                    {post.opinion_Tag}{post.opinion_Heading}
                                </h3>
                            </div>
                            <p onClick={() => goToDetails(post.id)} className="opinion-desc">{post.opinion_Description}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="load-more">
                {isFetching && <p>लोड हो रहा है...</p>}
                {!hasMore && <p>और कोई ओपिनियन नहीं</p>}
            </div>

            <style jsx>{`
                .page-title { color: #c10037; margin: 20px 10px 15px; font-size: 26px; font-weight: bold; text-align: center; }
                .opinion-list { padding: 10px; max-width: 1000px; margin: 0 auto; }

                /* Light Mode */
                .opinion-card.light { background: #ffffff; box-shadow: 0 3px 12px rgba(0,0,0,0.1); }
                .opinion-card.light .opinion-heading { color: #222; }
                .opinion-card.light .opinion-desc { color: #444; }

                /* Dark Mode */
                .opinion-card.dark { background: transparent; border: 0.5px solid #d3d3d3; }
                .opinion-card.dark .opinion-heading,
                .opinion-card.dark .opinion-desc { color: #f0f0f0; }
                .opinion-card.dark .opinion-tag { color: #ff3366; }

                .opinion-card { border-radius: 12px; overflow: hidden; margin-bottom: 20px; transition: all 0.3s ease; }

                .opinion-image { width: 100%; height: 220px; overflow: hidden; cursor: pointer; }
                .opinion-image img { width: 100%; height: 100%; object-fit: cover; transition: 0.4s; }
                .opinion-image:hover img { transform: scale(1.08); }

                .opinion-content { padding: 18px; }
                .opinion-tag { display: block; font-size: 18px; font-weight: 700; color: #c10037; text-transform: uppercase; margin-bottom: 6px; }
                .opinion-heading { font-size: 17px; font-weight: 700; line-height: 1.45; margin: 0; cursor: pointer; transition: color 0.3s; }
                .opinion-heading:hover { color: #c10037; }
                .opinion-desc { font-size: 14.5px; line-height: 1.6; font-weight:700 margin: 10px 0 0; display: none; }

                .load-more { text-align: center; padding: 35px; color: #666; font-size: 16px; }

                @media (min-width: 769px) {
                    .opinion-card { display: flex; }
                    .opinion-image { width: 240px; height: 150px; flex-shrink: 0; }
                    .opinion-content { padding: 22px; }
                    .opinion-tag { font-size: 16px; }
                    .opinion-heading { font-size: 18px; }
                    .opinion-desc { display: block !important; }
                    .opinion-card.dark .opinion-desc { color: #8e8e8e; }
                }
            `}</style>
        </>
    );
};

export default OpinionCard;